import {inject} from '@angular/core';
import {LOCATION_INITIALIZED} from '@angular/common';
import {TranslateService} from '@ngx-translate/core';
import {catchError} from 'rxjs/operators';
import {lastValueFrom, of} from 'rxjs';

import {I18N_CACHE_KEY} from './_constants/constants';
import {equalsAny} from './_utils/lang';

/**
 * Resolve the initial language using:
 *  1) ?lang= URL param
 *  2) localStorage
 *  3) browser language
 *  4) final fallback (en-CA)
 */
function resolveInitialLanguage(): string {
  // URL param
  try {
    if(typeof window !== 'undefined') {
      /*
       * The first time a user visits the page, check:
       *  1. if ?lang=... is set in the url
       *  2. if a valid language is saved in the localStorage
       *  3. if their browser specifies a preferred language.
       *      - If it does, and it happens to be one of the french languages, use french as our initial language.
       * Otherwise, english.
       * Once the user clicks on the switch language link, the chosen language is stored in a localStorage var.
       */
      const url = new URL(window.location.href);
      const lang = url.searchParams.get('lang');
      if(equalsAny(lang, 'fr-CA', 'en-CA')) {
        return lang!;
      }
    }
  } catch{
    /* ignore */
  }

  // localStorage
  try {
    const stored = localStorage.getItem(I18N_CACHE_KEY);
    if(equalsAny(stored, 'fr-CA', 'en-CA')) {
      return stored!;
    }
  } catch{
    /* ignore */
  }

  // browser language
  if(
    typeof navigator !== 'undefined' &&
    navigator.language?.startsWith('fr')
  ) {
    return 'fr-CA';
  }

  // hard fallback
  return 'en-CA';
}

/**
 * App initializer that ensures translations
 * are loaded before the first render.
 */
export function provideI18nInitializer() {
  return () => {
    const translate = inject(TranslateService);
    const locationInitialized =
      inject(LOCATION_INITIALIZED, {optional: true}) ??
      Promise.resolve(null);

    return locationInitialized.then(async() => {
      let lang = resolveInitialLanguage();

      translate.setDefaultLang(lang);

      try {
        await lastValueFrom(
          translate.use(lang).pipe(
            catchError(err => {
              console.error(
                `Failed to initialize language '${lang}', falling back to 'en-CA'.`,
                err
              );
              lang = 'en-CA';
              return translate.use(lang);
            })
          )
        );

        // Persist the resolved language
        try {
          localStorage.setItem(I18N_CACHE_KEY, lang);
        } catch{
          /* ignore */
        }

        console.info(`Language initialized: ${lang}`);
      } catch(err) {
        // Do not block bootstrap under any circumstance
        console.error('i18n initialization failed; continuing bootstrap', err);
        await lastValueFrom(
          translate.use('en-CA').pipe(catchError(() => of('en-CA')))
        );
      }
    });
  };
}
